import { chunkArray } from '@/helpers/arrayHelpers'
import { debounce } from '@/helpers/debounce'
import { v4 as uuidv4 } from 'uuid'
import constants from '@/helpers/constants'

const MAX_ARRAY_SIZE = 20
const CART_UID_KEY = 'cartUid'

export const GoogleTagManagerService = {
  productsInViewPort: [],
  promotionsInViewPort: [],
  createTemplate (ecommerceEventName, dataKey, { analyticsData = {}, actionField = null, params = {} } = {}) {
    return {
      'ecommerce': {
        'currencyCode': window.config?.currency?.currency_code,
        ...(ecommerceEventName !== 'impressions'
          ? {
            [ecommerceEventName]: {
              [dataKey]: analyticsData,
              ...(actionField && { 'actionField': actionField })
            }
          }
          : {
            [ecommerceEventName]: analyticsData
          })
      },
      'gtm-ee-event-category': 'Enhanced Ecommerce',
      'gtm-ee-event-non-interaction': `False`,
      ...params
    }
  },
  pushToDatalayer (tmp) {
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push(tmp)
  },
  generalHandler (ecommerceEventName, dataKey, dataObj) {
    return new Promise((resolve, reject) => {
      const chunkedData = chunkArray(MAX_ARRAY_SIZE, dataObj.analyticsData)
      chunkedData.forEach(item => {
        const template = this.createTemplate(ecommerceEventName, dataKey, { ...dataObj, analyticsData: item })
        this.pushToDatalayer(template)
      })
      resolve()
    }).catch(() => {
      return Promise.resolve()
    })
  },
  checkoutEvent ({ analyticsData, actionField, params }) {
    return this.generalHandler('checkout', 'products', { analyticsData, actionField, params })
  },
  purchaseEvent ({ analyticsData, actionField, params }) {
    return this.generalHandler('purchase', 'products', { analyticsData, actionField, params })
  },
  addToCartEvent ({ analyticsData, params }) {
    return this.generalHandler('add', 'products', { analyticsData, params })
  },
  removeFromCartEvent ({ analyticsData, params }) {
    return this.generalHandler('remove', 'products', { analyticsData, params })
  },
  promotionViewEvent ({ analyticsData }) {
    const params = {
      'event': 'promotionView'
    }
    this.promotionsInViewPort = [...this.promotionsInViewPort, ...analyticsData]
    this.promotionViewEventAction({ params })
  },
  promotionViewEventAction: debounce(function ({ params }) {
    this.generalHandler('promoView', 'promotions', {
      analyticsData: this.promotionsInViewPort,
      params
    })
    this.promotionsInViewPort = []
  }, 100),
  promotionClickEvent ({ analyticsData = {}, params = {} } = {}) {
    params = {
      'event': 'promotionClick',
      ...params
    }
    return this.generalHandler('promoClick', 'promotions', { analyticsData, params })
  },
  // then для цього метода не працює :(
  productViewEvent ({ analyticsData }) {
    const params = {
      'event': 'gtm-ee-event-impressions',
      'gtm-ee-event-action': 'Product Impressions',
      'gtm-ee-event-non-interaction': 'True'
    }
    this.productsInViewPort = [...this.productsInViewPort, ...analyticsData]
    this.productViewEventAction({ params })
  },

  productViewEventAction: debounce(function ({ params }) {
    this.generalHandler('impressions', 'products', {
      analyticsData: this.productsInViewPort,
      params
    })
    this.productsInViewPort = []
  }, 100),

  productClickEvent ({ analyticsData, actionField }) {
    const params = {
      'event': `gtm-ee-event-product-click`,
      'gtm-ee-event-action': `Product Clicks`
    }
    return this.generalHandler('click', 'products', { analyticsData, actionField, params })
  },
  productInfoEvent ({ analyticsData, actionField }) {
    const params = {
      'event': 'gtm-ee-event-product-details',
      'gtm-ee-event-action': 'Product Details',
      'gtm-ee-event-non-interaction': `True`
    }
    return this.generalHandler('detail', 'products', {
      analyticsData,
      actionField,
      params
    })
  },
  pushToAnalytics ({ analyticsData, eventName, additionalFields }) {
    let listForAnalytics = sessionStorage.getItem(`listForAnalytics`)
    const item_list_name = listForAnalytics || ''
    const { currency_code: currency } = window.config.currency
    const items = analyticsData.map(item => {
      const {
        category: item_category,
        id: item_id,
        price,
        name: item_name,
        quantity
      } = item
      return {
        item_name,
        item_id,
        price,
        currency,
        item_category,
        item_list_name,
        quantity: quantity || 1
      }
    })
    const result = {
      event: eventName,
      ecommerce: {
        items
      }
    }
    if (additionalFields && Object.keys(additionalFields).length) {
      Object.keys(additionalFields).forEach(el => {
        result[el] = additionalFields[el]
      })
    }
    this.pushToDatalayer({ ecommerce: null })
    this.pushToDatalayer(result)
  },
  formAnalytics ({ formName, fieldName, action, value, additionalFields = {} }) {
    let result = {
      event: `form_${formName}_${action}`
    }
    if (fieldName) {
      result.event = `form_${formName}_${fieldName}_${action}`
    }
    if (value) {
      result.value = value
    }
    result = {
      ...result,
      ...additionalFields
    }
    this.pushToDatalayer(result)
  },
  searchAnalytics ({ query }) {
    // window.gtag('event', 'search', {
    //   search_term: query
    // })
    const result = {
      event: 'search',
      search_term: query
    }
    this.pushToDatalayer(result)
  },
  signUpAnalytics ({ method }) {
    // window.gtag('event', 'sign_up', {
    //   method: method
    // })
    const result = {
      event: 'sign_up',
      method: method
    }
    this.pushToDatalayer(result)
  },
  loginAnalytics ({ method }) {
    // window.gtag('event', 'login', {
    //   method: method
    // })
    const result = {
      event: 'login',
      method: method
    }
    this.pushToDatalayer(result)
  },
  btnClickAnalytics ({ event }) {
    this.pushToDatalayer({
      event
    })
  },
  selectItemAnalytics ({ analyticsData }) {
    const {
      id: item_id,
      item_name,
      discount,
      index,
      category_name: item_category,
      price
    } = analyticsData
    const result = {
      event: 'select_item',
      items: [
        {
          item_id,
          item_name,
          discount,
          index,
          item_category,
          price
        }
      ]
    }
    this.pushToDatalayer(result)
    // window.gtag('event', 'select_item', {
    //   items: [
    //     {
    //       item_id,
    //       item_name,
    //       discount,
    //       index,
    //       item_category,
    //       price
    //     }
    //   ]
    // })
  },
  viewItemListAnalytics ({ analyticsData, listId, listName }) {
    const list = analyticsData.map(el => {
      const {
        id: item_id,
        item_name,
        discount,
        index,
        category_name: item_category,
        price
      } = el
      return {
        item_id,
        item_name,
        discount,
        index,
        item_category,
        price
      }
    })
    // window.gtag('event', 'view_item_list', {
    //   item_list_id: listId,
    //   item_list_name: listName,
    //   items: list
    // })
    const result = {
      event: 'view_item_list',
      item_list_id: listId,
      item_list_name: listName,
      items: list
    }
    this.pushToDatalayer(result)
  }
}
export const GoogleEnhancedEcommerce = {
  isEnabled: true,
  getGaClientId () {
    try {
      const match = document.cookie.match(/(?:^|; )_ga=([^;]*)/)
      if (!match || !match[1]) return null
      const parts = match[1].split('.')
      if (parts.length < 4) return null
      return `${parts[2]}.${parts[3]}`
    } catch (e) {
      return null
    }
  },
  getGaSessionId () {
    try {
      const measurementId = window.config?.params?.google_settings?.ga4?.measurement_id || 'G-R2CVLVEMEM'
      const cookieName = `_ga_${measurementId.replace(/^G-/, '')}`
      const match = document.cookie.match(new RegExp(`(?:^|; )${cookieName}=([^;]*)`))
      if (!match || !match[1]) return null
      const parts = match[1].split('.')
      return parts[2] || null
    } catch (e) {
      return null
    }
  },
  normalizePhone (phone) {
    if (!phone) return ''
    return phone.toString().replace(/\D+/g, '')
  },
  normalizeEmail (email) {
    if (!email) return ''
    return email.toString().trim().toLowerCase()
  },
  rememberExternalUserId (userId) {
    if (!userId) return ''

    const normalizedUserId = userId.toString()
    localStorage.setItem(constants.ANALYTICS_USER_ID_KEY, normalizedUserId)

    return normalizedUserId
  },
  getStoredExternalUserId () {
    return localStorage.getItem(constants.ANALYTICS_USER_ID_KEY) || ''
  },
  resolveExternalUserId (userId = '') {
    return userId ? this.rememberExternalUserId(userId) : this.getStoredExternalUserId()
  },
  getAnalyticsUserId ({ phone = '', email = '', externalUserId = '' } = {}) {
    const normalizedPhone = this.normalizePhone(phone)
    if (normalizedPhone) return normalizedPhone

    const normalizedEmail = this.normalizeEmail(email)
    if (normalizedEmail) return normalizedEmail

    const resolvedExternalUserId = this.resolveExternalUserId(externalUserId)
    if (resolvedExternalUserId) return resolvedExternalUserId

    return ''
  },
  pushUserIdentity ({ userId, phone = '', email = '', logged = 'no', userType = 'Client', employeeCompany = 'no', event = '' } = {}) {
    if (userId) {
      this.rememberExternalUserId(userId)
    }
    const resolvedUserId = this.getAnalyticsUserId({ phone, email, externalUserId: userId })
    const data = {
      logged,
      userType,
      employee_company: employeeCompany
    }
    if (event) {
      data.event = event
    }

    if (resolvedUserId) {
      data.userId = resolvedUserId
      data.user_id = resolvedUserId
    }

    this.pushToDatalayer(data)
  },
  getAnalyticsFormData ({ phone = '', email = '', externalUserId = '', items = {} } = {}) {
    const analyticsDataReferrer = localStorage.getItem('analyticsDataReferrer')
      ? JSON.parse(localStorage.getItem('analyticsDataReferrer'))
      : {}

    const gaSessionId = this.getGaSessionId()

    return {
      ...analyticsDataReferrer,
      ga_client_id: this.getGaClientId(),
      ga_session_id: gaSessionId,
      session_id: gaSessionId,
      user_id: this.getAnalyticsUserId({ phone, email, externalUserId }),
      email: this.normalizeEmail(email),
      phone: this.normalizePhone(phone),
      items
    }
  },
  saveCollectedData (result) {
    localStorage.setItem('analyticsDataReferrer', JSON.stringify(result))
  },
  collectUserAnalyticsData () {
    const defaultReferers = {
      '360.cn': '360.cn',
      'www.alice.com': 'Alice',
      'aliceadsl.fr': 'Alice',
      'www.alltheweb.com': 'Alltheweb',
      'www.altavista.com': 'Altavista',
      'www.aol.com': 'AOL',
      'www.ask.com': 'Ask',
      'search.aol.fr': 'Ask',
      'alicesuche.aol.de': 'Ask',
      'search.auone.jp': 'Auone',
      'isearch.avg.com': 'Avg',
      'search.babylon.com': 'Babylon',
      'www.baidu.com': 'Baidu',
      'biglobe.ne.jp': 'Biglobe',
      'www.bing.com': 'bing',
      'search.centrum.cz': 'Centrum.cz',
      'search.comcast.net': 'Comcast',
      'search.conduit.com': 'Conduit',
      'www.cnn.com/SEARCH': 'CNN',
      'www.daum.net': 'Daum',
      'duckduckgo.com': 'DuckDuckGo',
      'www.ecosia.org': 'Ecosia',
      'www.ekolay.net': 'Ekolay',
      'www.eniro.se': 'Eniro',
      'www.globo.com/busca': 'Globo',
      'go.mail.ru': 'go.mail.ru',
      'www.google.com': 'google',
      'www.google.co.uk': 'google',
      'goo.ne.jp': 'goo.ne',
      'www.haosou.com/s': 'haosou.com',
      'search.incredimail.com': 'Incredimail',
      'www.kvasir.no': 'Kvasir',
      'www.lycos.com': 'Lycos',
      'search.lycos.de': 'Lycos',
      'www.mamma.com': 'Mamma',
      'www.msn.com': 'MSN',
      'money.msn.com': 'MSN',
      'local.msn.com': 'MSN',
      'www.mynet.com': 'MSN',
      'najdi.si': 'Najdi',
      'www.naver.com': 'Naver',
      'search.netscape.com': 'Netscape',
      'szukaj.onet.pl': 'ONET',
      'www.ozu.es': 'Ozu',
      'www.qwant.com': 'Qwant',
      'rakuten.co.jp': 'Rakuten',
      'rambler.ru': 'rambler',
      'search-results.com': 'Search-results',
      'search.smt.docomo.ne.jp': 'search.smt.docomo',
      'sesam.no': 'Sesam',
      'www.seznam.cz': 'Seznam',
      'www.so.com/s': 'So.com',
      'www.sogou.com': 'Sogou',
      'www.startsiden.no/sok': 'Startsiden',
      'www.szukacz.pl': 'Szukacz',
      'buscador.terra.com.br': 'Terra',
      'search.tut.by': 'Tut.by',
      'search.ukr.net': 'Ukr',
      'search.virgilio.it': 'Virgilio',
      'www.voila.fr': 'Voila',
      'www.wp.pl': 'Wirtualna Polska',
      'www.yahoo.com': 'yahoo',
      'yahoo.cn': 'yahoo',
      'm.yahoo.com': 'yahoo',
      'www.yandex.com': 'yandex',
      'yandex.ru': 'yandex',
      'www.yam.com': 'yam'
    }
    const url = new URL(window.location.href)
    const params = Object.fromEntries(url.searchParams.entries())
    const referer = document.referrer.replace(/^https?:\/\//, '').replace(/\/$/, '')
    let result = params
    // google
    if (params.gclid || params.wbraid || params.gbraid) {
      result = {
        ...result,
        utm_source: params.utm_source || 'google',
        utm_medium: params.utm_medium || 'cpc_server'
      }
      this.saveCollectedData(result)
    } else if (params.fbclid && referer.includes('facebook')) { // facebook
      result = {
        ...result,
        utm_source: params.utm_source || 'facebook',
        utm_medium: params.utm_medium || 'MP_server'
      }
      this.saveCollectedData(result)
    } else if (params.fbclid && referer.includes('instagram')) { // instagram
      result = {
        ...result,
        utm_source: params.utm_source || 'instagram',
        utm_medium: params.utm_medium || 'MP_server'
      }
      this.saveCollectedData(result)
    } else if (params.fbclid && !referer.includes('instagram') && !referer.includes('facebook')) { // facebook_instagram
      result = {
        ...result,
        utm_source: params.utm_source || 'facebook_instagram',
        utm_medium: params.utm_medium || 'MP_server'
      }
      this.saveCollectedData(result)
    } else if (defaultReferers[referer] && !params.utm_source && !params.utm_medium && !params.fbclid && !params.gclid && !params.wbraid && !params.gbraid) { // organic
      result = {
        utm_source: defaultReferers[referer],
        utm_medium: 'organic_server'
      }
      this.saveCollectedData(result)
    } else if (referer && !defaultReferers[referer] && (!params.utm_source && !params.utm_medium) && (!params.fbclid || !params.gclid || !params.wbraid || !params.gbraid)) { // referral
      result = {
        utm_source: referer,
        utm_medium: 'referral_server'
      }
      this.saveCollectedData(result)
    } else if ((!params.fbclid || !params.gclid || !params.wbraid || !params.gbraid) && ((params.utm_source && params.utm_medium) && (params.utm_content || params.utm_term || params.utm_campaign))) { // utm
      result = {
        utm_source: params.utm_source,
        utm_medium: params.utm_medium
      }
      if (params.utm_campaign) {
        result.utm_campaign = params.utm_campaign
      }
      if (params.utm_content) {
        result.utm_content = params.utm_content
      }
      if (params.utm_term) {
        result.utm_term = params.utm_term
      }
      this.saveCollectedData(result)
    } else if (!params.utm_source && !params.utm_medium && !referer && (!params.fbclid || !params.gclid || !params.gbraid || !params.wbraid)) { // direct
      result = {
        utm_source: '(direct)',
        utm_medium: '(none)_server'
      }
      this.saveCollectedData(result)
    }
  },
  getGoogleEnhancedEcommerceData (currData) {
    let itemData = {
      'item_name': currData.name_uk,
      'item_id': currData.id, // https://imgur.com/TtuDO5O передаємо ідентифікатор як у фіді Google Merchant
      'price': +currData.price, // https://imgur.com/juehVh8
      'discount': +(parseFloat(currData.discount).toFixed(2)) || 0, // https://imgur.com/vtrNxWK
      'currency': 'PLN', // Валюта
      'item_brand': 'Dnipro-M', // Бренд товару
      'quantity': currData.quantity || 1 // Кількість одиниць товару
    }
    if (currData.categories) {
      currData.categories.forEach((el, index) => {
        let name = 'item_category'
        if (index > 0) {
          name = 'item_category' + parseInt(index + 1)
        }
        itemData[name] = el.name_uk
        itemData[name + '_pl'] = el.name_pl || el.name
      })
    }
    if (currData.option && currData.option.value) {
      itemData.item_variant = currData.option.value
    }
    const promoBannerData = this.getPromoBannerData(currData.id)
    if (Object.keys(promoBannerData).length) {
      itemData.promotion_name = promoBannerData.promotion_name
      itemData.promotion_id = promoBannerData.promotion_id.toString()
      itemData.creative_slot = promoBannerData.creative_slot
    }
    itemData = { ...itemData, ...this.getSavedListData(currData.id) }
    return itemData
  },
  getPromoBannerData (code) {
    const savedBanners = JSON.parse(localStorage.getItem('clickedBanners')) || []
    const bannerPromoItems = JSON.parse(localStorage.getItem('bannerPromoItems')) || {}
    for (let key in bannerPromoItems) {
      const promoItem = bannerPromoItems[key].find(el => el.toString() === code.toString())
      if (promoItem) {
        return savedBanners.find(el => el.promotion_id.toString() === key.toString()) || {}
      }
    }
    return {}
  },
  getSavedListData (itemId) {
    let savedLists = JSON.parse(localStorage.getItem('savedLists')) || []
    let result = {}
    savedLists.forEach(el => {
      const hasItemId = el.items.find(el => el === itemId)
      if (hasItemId) {
        result = {
          item_list_id: el.item_list_id,
          item_list_name: el.item_list_name
        }
      }
    })
    return result
  },
  async buyInAllegro () {
    this.pushToDatalayer({ ecommerce: null })
    this.pushToDatalayer({
      event: 'buy_in_Allegro'
    })
  },
  async clickFooterAllegro () {
    this.pushToDatalayer({ ecommerce: null })
    this.pushToDatalayer({
      event: 'click_footer_Allegro'
    })
  },
  setList (list) {
    let savedLists = JSON.parse(localStorage.getItem('savedLists')) || []
    let sameList = savedLists.find(el => el.item_list_id === list.item_list_id)
    const sameListIndex = savedLists.findIndex(el => el.id === list.item_list_id)
    // чтоб отправлять только ПОСЛЕДНИЙ список в котором был товар, мы убираем id товара с всех других списков перед добавлением
    savedLists.forEach(el => {
      list.items.forEach(listId => {
        const oldIdInOldListIndex = el.items.findIndex(id => id === listId)
        if (oldIdInOldListIndex > -1) {
          el.items.splice(oldIdInOldListIndex, 1)
        }
      })
    })
    if (sameList) {
      sameList.items = [...sameList.items, ...list.items]
      savedLists[sameListIndex] = sameList
    } else {
      savedLists.push(list)
    }
    localStorage.setItem('savedLists', JSON.stringify(savedLists))
  },
  pushToDatalayer (tmp) {
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push(tmp)
  },
  emailSubscription ({ userId = '', phone = '', email = '' } = {}) {
    const resolvedUserId = this.getAnalyticsUserId({ phone, email, externalUserId: userId })
    const data = {
      event: 'email_subscription'
    }

    if (resolvedUserId) {
      data.user_id = resolvedUserId
      data.userId = resolvedUserId
    }

    this.pushToDatalayer(data)
  },
  viewPromotion (data) {
    this.pushToDatalayer({ ecommerce: null })
    this.pushToDatalayer({
      event: 'view_promotion',
      ecommerce: data
    })
  },
  selectPromotion (data) {
    this.pushToDatalayer({ ecommerce: null })
    this.pushToDatalayer({
      event: 'select_promotion',
      ecommerce: data
    })
  },
  selectItem (data) {
    this.pushToDatalayer({ ecommerce: null })
    this.pushToDatalayer({
      event: 'select_item',
      ecommerce: data
    })
  },
  viewItem (data) {
    this.pushToDatalayer({ ecommerce: null })
    this.pushToDatalayer({
      event: 'view_item',
      ecommerce: data
    })
  },
  viewItemList ({ analyticsData, listId, listName }) {
    const data = analyticsData.map(el => {
      return {
        ...el,
        item_list_id: listId || 'відсутній',
        item_list_name: listName
      }
    })
    const result = {
      event: 'view_item_list',
      ecommerce: {
        items: data
      }
    }
    this.setList({
      item_list_id: listId,
      item_list_name: listName,
      items: analyticsData.map(el => el.item_id)
    })
    this.pushToDatalayer(result)
  },
  dinRemViewItemList ({ analyticsData, listName, userId }) {
    const prices = analyticsData.map(el => el.price)
    const summ = analyticsData.reduce((acc, curr) => acc + curr.price * (curr.quantity || 1), 0)
    const ids = analyticsData.map(el => el.item_id)
    this.pushToDatalayer(
      {
        'event': 'din_rem_view_item_list',
        'dynamicParams': {
          'ecomm_prodid': ids,
          'ecomm_item_price': prices,
          'ecomm_pagetype': 'category',
          'ecomm_totalvalue': summ,
          'ecomm_category': listName,
          'ecomm_customer_id': userId.toString()
        }
      }
    )
  },
  dinRemSearchResults ({ analyticsData, userId }) {
    const prices = analyticsData.map(el => el.price)
    const summ = analyticsData.reduce((acc, curr) => acc + curr.price * (curr.quantity || 1), 0)
    const ids = analyticsData.map(el => el.item_id)
    const categories = analyticsData.map(el => el.item_category2_pl || el.item_category_pl)
    this.pushToDatalayer(
      {
        'event': 'din_rem_searchresults',
        'dynamicParams': {
          'ecomm_prodid': ids,
          'ecomm_item_price': prices,
          'ecomm_pagetype': 'searchresults',
          'ecomm_totalvalue': summ,
          'ecomm_category': categories,
          'ecomm_customer_id': userId.toString()
        }
      }
    )
  },
  dinRemBeginCheckout ({ items, userId }) {
    const prices = items.map(el => el.price)
    const summ = items.reduce((acc, curr) => acc + curr.price * (curr.quantity || 1), 0)
    const ids = items.map(el => el.item_id)
    const categories = items.map(el => el.item_category2_pl || el.item_category_pl)
    const quantity = items.map(el => el.quantity)
    this.pushToDatalayer(
      {
        'event': 'din_rem_begin_checkout',
        'dynamicParams': {
          'ecomm_prodid': ids,
          'ecomm_item_price': prices,
          'ecomm_pagetype': 'cart',
          'ecomm_totalvalue': summ,
          'ecomm_category': categories,
          'ecomm_quantity': quantity,
          'ecomm_customer_id': userId.toString()
        }
      }
    )
  },
  dinRemPurchase ({ items, userId }) {
    const prices = items.map(el => el.price)
    const summ = items.reduce((acc, curr) => acc + curr.price * (curr.quantity || 1), 0)
    const ids = items.map(el => el.item_id)
    const categories = items.map(el => el.item_category2_pl || el.item_category_pl)
    const quantity = items.map(el => el.quantity)
    this.pushToDatalayer(
      {
        'event': 'din_rem_purchase',
        'dynamicParams': {
          'ecomm_prodid': ids,
          'ecomm_item_price': prices,
          'ecomm_pagetype': 'purchase',
          'ecomm_totalvalue': summ,
          'ecomm_quantity': quantity,
          'ecomm_category': categories,
          'ecomm_customer_id': userId.toString()
        }
      }
    )
  },
  addToCart (data) {
    this.pushToDatalayer({ ecommerce: null })
    this.pushToDatalayer({
      event: 'add_to_cart',
      ecommerce: data
    })
  },
  removeFromCart (data) {
    this.pushToDatalayer({ ecommerce: null })
    this.pushToDatalayer({
      event: 'remove_from_cart',
      ecommerce: data
    })
  },
  addToWishlist (data) {
    this.pushToDatalayer({ ecommerce: null })
    this.pushToDatalayer({
      event: 'add_to_wishlist',
      ecommerce: data
    })
  },
  beginCheckout (data) {
    this.pushToDatalayer({ ecommerce: null })
    this.pushToDatalayer({
      event: 'begin_checkout',
      ecommerce: data
    })
  },
  contactInformation (data) {
    this.pushToDatalayer({ ecommerce: null })
    this.pushToDatalayer({
      event: 'contact_information',
      ecommerce: data
    })
  },
  addShippingInfo (data) {
    this.pushToDatalayer({ ecommerce: null })
    this.pushToDatalayer({
      event: 'add_shipping_info',
      ecommerce: data
    })
  },
  addPaymentInfo (data) {
    this.pushToDatalayer({ ecommerce: null })
    this.pushToDatalayer({
      event: 'add_payment_info',
      ecommerce: data
    })
  },
  purchase (data) {
    this.pushToDatalayer({ ecommerce: null })
    this.pushToDatalayer({
      event: 'purchase',
      ecommerce: data
    })
  }
}

export const EsputnikService = {
  createGeneralInfoTemplate (userData = {}) {
    return {
      'user_email': userData.email ?? '',
      'user_phone': userData.phone ?? '',
      'user_es_contact_id': this.getEsUserId()
    }
  },
  createCustomerDataTemplate ({ userData = {}, selectedUserLocation = {} }) {
    return {
      'user_email': userData.email ?? '',
      'user_name': userData.name ?? '',
      'user_client_id': userData.user_id ?? '',
      'user_phone': userData.phone ?? '',
      'user_city': selectedUserLocation.name ?? ''
    }
  },
  createCartProductsTemplate ({ products }) {
    return products.map(item => {
      return {
        'productKey': item.id ?? '',
        'price': item.price ?? '',
        'quantity': item.quantity ?? '',
        'currency': window.config?.currency?.currency_code
      }
    })
  },
  createProductInfoTemplate ({ product }) {
    return {
      'productKey': product.productKey ?? '',
      'price': product.price ?? '',
      'isInStock': product.isInStock ? '1' : '0'
    }
  },
  createProductImpressionsTemplate ({ products }) {
    return products.map(item => {
      return {
        'product_id': item.id,
        'container_type': item.container_type
      }
    })
  },
  eventWrapper ({ mainEventName = 'sendEvent', eventName, data = {} }) {
    if (!window.config?.login_as_user) {
      const parsedDataFromLocalStorage = JSON.parse(localStorage.getItem(constants.USER_DATA_KEY) ?? null) ?? {}
      let generalInfo = {
        'GeneralInfo': this.createGeneralInfoTemplate(parsedDataFromLocalStorage)
      }
      if (Object.keys(data).length) {
        if (data.hasOwnProperty('GeneralInfo')) {
          generalInfo.GeneralInfo = {
            ...data.GeneralInfo,
            ...generalInfo.GeneralInfo
          }
          delete data.GeneralInfo
        }
        data = {
          ...generalInfo,
          ...data
        }
      } else {
        data = generalInfo
      }

      window.eS(mainEventName, eventName, data)
    }
  },
  sendProductPageInfo ({ product, blockId }) {
    const data = {
      'ProductPage': this.createProductInfoTemplate({ product })
    }

    if (blockId) {
      data['GeneralInfo'] = {}
      data['GeneralInfo']['s_location'] = blockId
    }

    this.eventWrapper({
      eventName: 'ProductPage',
      data
    })
  },
  sendCategoryPageInfo ({ categoryName }) {
    this.eventWrapper({
      eventName: 'CategoryPage',
      data: {
        'CategoryPage': {
          'categoryKey': categoryName
        }
      }
    })
  },
  sendErrorPageInfo () {
    this.eventWrapper({ eventName: 'NotFound' })
  },
  sendMainPageInfo () {
    this.eventWrapper({ eventName: 'MainPage' })
  },
  sendStatusCartInfo ({ products }) {
    const data = {
      'StatusCart': this.createCartProductsTemplate({ products }),
      'GUID': this.generateCartUid()
    }

    this.eventWrapper({
      eventName: 'StatusCart',
      data
    })
  },
  sendPurchasedItemsInfo ({ products, orderNumber, isCheckout = true, tags = null }) {
    const data = {
      'PurchasedItems': this.createCartProductsTemplate({ products }),
      'OrderNumber': orderNumber
    }

    if (isCheckout) data['GUID'] = this.getCurrentCartUid()

    if (tags) data['Tags'] = tags

    // TODO Отправка заказов должна біть с бека. Проверить синхрон пользователей по ИД, что бі правильно работала брошеная корзина
    this.eventWrapper({
      eventName: 'PurchasedItems',
      data
    })
  },
  sendCustomerDataInfo ({ userData, selectedUserLocation }) {
    this.eventWrapper({
      eventName: 'CustomerData',
      userData,
      data: {
        'CustomerData': this.createCustomerDataTemplate({ userData, selectedUserLocation })
      }
    })
  },
  sendProductImpressions ({ products }) {
    this.eventWrapper({
      eventName: 'ProductImpressions',
      data: {
        'ProductImpression': this.createProductImpressionsTemplate({ products })
      }
    })
  },
  generateCartUid () {
    const uid = uuidv4()

    localStorage.setItem(CART_UID_KEY, uid)

    return uid
  },
  getCurrentCartUid () {
    return localStorage.getItem(CART_UID_KEY) ?? ''
  },
  getEsUserId () {
    return localStorage.getItem('esWebPushContactId') ?? ''
  },
  getRecommendations (params) {
    return new Promise((resolve, reject) => {
      window.eS('getRecommendations', params,
        function (error, products) {
          if (error) reject(error)
          resolve(products)
        })
    })
  },
  triggerEsEvent (eventName, params) {
    window.eS(eventName, params)
  },
  addToWishlist (eventName, data) {
    window.eS('sendEvent', eventName, data)
  }
}

export const RtbHouseService = {
  pushToRtbhEvents (data) {
    window.rtbhEvents = window.rtbhEvents || []
    window.rtbhEvents.push(...data)
  },
  getUid () {
    if (!window.config?.login_as_user) {
      return JSON.parse(localStorage.getItem(constants.USER_DATA_KEY))?.user_id || 'unknown'
    }
    return 'unknown'
  },
  HomePageCode () {
    this.pushToRtbhEvents(
      [
        {
          eventType: 'home'
        },
        {
          eventType: 'uid',
          id: this.getUid()
        }
      ]
    )
  },
  CategoryPageCode (categoryId) {
    this.pushToRtbhEvents(
      [
        {
          eventType: 'category',
          categoryId: categoryId
        },
        {
          eventType: 'uid',
          id: this.getUid()
        }
      ]
    )
  },
  SaleOrPromoCode () {
    this.pushToRtbhEvents(
      [
        {
          eventType: 'sales'
        },
        {
          eventType: 'uid',
          id: this.getUid()
        }
      ]
    )
  },
  NewOffersPageCode () {
    this.pushToRtbhEvents([
      {
        eventType: 'newoffers'
      },
      {
        eventType: 'uid',
        id: this.getUid()
      }
    ])
  },
  ProductOfferPageCode (offerId) {
    this.pushToRtbhEvents([
      {
        eventType: 'offer',
        offerId: offerId
      },
      {
        eventType: 'uid',
        id: this.getUid()
      }
    ])
  },
  ShoppingCartStatusCode (ids) {
    this.pushToRtbhEvents([
      {
        eventType: 'basketstatus',
        offerIds: ids
      },
      {
        eventType: 'uid',
        id: this.getUid()
      }
    ])
  },
  WishlistCode (id) {
    this.pushToRtbhEvents([
      {
        eventType: 'wishlist',
        offerId: id
      },
      {
        eventType: 'uid',
        id: this.getUid()
      }
    ])
  },
  SearchResultPageCode (ids) {
    this.pushToRtbhEvents([
      {
        eventType: 'listing',
        offerIds: ids
      },
      {
        eventType: 'uid',
        id: this.getUid()
      }
    ])
  },
  ShoppingCartAddToCartCode (id) {
    this.pushToRtbhEvents([
      {
        eventType: 'basketadd',
        offerId: id
      },
      {
        eventType: 'uid',
        id: this.getUid()
      }
    ])
  },
  OrderProcessStartCode () {
    this.pushToRtbhEvents([
      {
        eventType: 'startorder'
      },
      {
        eventType: 'uid',
        id: this.getUid()
      }
    ])
  },
  MakeAPurchase ({ ids, orderId, value, currency }) {
    this.pushToRtbhEvents([
      {
        eventType: 'conversion',
        conversionClass: 'order',
        conversionSubClass: 'purchase',
        conversionId: orderId,
        offerIds: ids,
        conversionValue: value,
        conversionCurrency: currency
      },
      {
        eventType: 'uid',
        id: this.getUid()
      }
    ])
  },
  OtherPages () {
    this.pushToRtbhEvents([
      {
        eventType: 'placebo'
      },
      {
        eventType: 'uid',
        id: this.getUid()
      }
    ])
  }
}
